import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Link,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

// Generic transaction type for regular account transactions
export interface BaseTransaction {
  transactionId: string;
  dateInitiated?: string;
  date?: string;
  type?: string;
  action?: string;
  subject?: string;
  amount?: number;
  rawType?: string;
  rawTransactionType?: string;
  toUser?: string;
  fromUser?: string;
  requiredPostage?: number;
  postage?: number;
  attachedFunds?: number;
  attachedAmount?: number;
  postageSystemFee?: number;
  attachedFundsFee?: number;
  reservedFunds?: number;
  balanceBefore?: number;
  availableBalanceAfter?: number;
  actualBalanceAfter?: number;
  commercialCategory?: string;
  vendorId?: string;
  postageRateType?: string;
  [key: string]: any; // Allow additional properties
}

// Generic transaction type for promo credit transactions
export interface BasePromoTransaction {
  transactionId: string;
  transactionDate: string;
  transactionType: string;
  sponsorUsername: string;
  campaignName?: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  description?: string;
  referenceId?: string;
  referenceType?: string;
  username?: string;
  [key: string]: any; // Allow additional properties
}

// Column definition for sortable columns
export interface SortableColumn<T> {
  id: keyof T | string;
  label: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  width?: number | string;
}

// Transaction type filter option
export interface TransactionTypeOption {
  value: string;
  label: string;
}

// Props for the search/filter component
export interface TransactionSearchFilterProps<T> {
  // Data
  transactions: T[];
  transactionTypes?: TransactionTypeOption[];
  
  // State handlers - parent component owns the state
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  dateRangeStart: Date | null;
  dateRangeEnd: Date | null;
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
  typeFilter: string;
  onTypeFilterChange: (type: string) => void;
  
  // Sort state
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSortChange: (column: string, direction: 'asc' | 'desc') => void;
  
  // Show/hide toggle state
  isExpanded: boolean;
  onToggleExpand: () => void;
  
  // CSV Export
  csvFilename?: string;
  csvHeaders?: string[];
  getCsvRow?: (tx: T) => string[];
  
  // Filtering functions (can be customized by parent)
  filterFn?: (tx: T, query: string, startDate: Date | null, endDate: Date | null, typeFilter: string) => boolean;
  
  // Date field accessor (defaults to 'dateInitiated' or 'transactionDate')
  getDateField?: (tx: T) => string;
  
  // Custom title
  title?: string;
  
  // Additional controls to render
  additionalControls?: React.ReactNode;
}

// Default filter function for regular transactions
export function defaultRegularTransactionFilter<T extends BaseTransaction>(
  tx: T,
  query: string,
  startDate: Date | null,
  endDate: Date | null,
  typeFilter: string
): boolean {
  // Text search
  if (query.trim()) {
    const q = query.toLowerCase();
    const searchableFields = [
      tx.subject,
      tx.type,
      tx.action,
      tx.toUser,
      tx.fromUser,
      tx.transactionId,
      tx.rawType,
      tx.rawTransactionType,
      tx.commercialCategory,
    ].filter(Boolean).map(s => s?.toLowerCase() || '');
    
    if (!searchableFields.some(f => f.includes(q))) {
      return false;
    }
  }
  
  // Date range filter
  const txDate = new Date(tx.dateInitiated || tx.date || 0);
  if (startDate && txDate < startDate) return false;
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (txDate > end) return false;
  }
  
  // Type filter
  if (typeFilter && tx.type !== typeFilter && tx.rawType !== typeFilter && tx.rawTransactionType !== typeFilter) {
    return false;
  }
  
  return true;
}

// Default filter function for promo transactions
export function defaultPromoTransactionFilter<T extends BasePromoTransaction>(
  tx: T,
  query: string,
  startDate: Date | null,
  endDate: Date | null,
  typeFilter: string
): boolean {
  // Text search
  if (query.trim()) {
    const q = query.toLowerCase();
    const searchableFields = [
      tx.sponsorUsername,
      tx.campaignName,
      tx.description,
      tx.transactionType,
      tx.transactionId,
      tx.username,
      tx.referenceType,
    ].filter(Boolean).map(s => s?.toLowerCase() || '');
    
    if (!searchableFields.some(f => f.includes(q))) {
      return false;
    }
  }
  
  // Date range filter
  const txDate = new Date(tx.transactionDate || 0);
  if (startDate && txDate < startDate) return false;
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (txDate > end) return false;
  }
  
  // Type filter
  if (typeFilter && tx.transactionType !== typeFilter) {
    return false;
  }
  
  return true;
}

// Default sort function
export function defaultSort<T>(
  data: T[],
  column: string,
  direction: 'asc' | 'desc',
  getDateField?: (tx: T) => string
): T[] {
  if (!column) return data;
  
  return [...data].sort((a, b) => {
    let aVal: any = (a as any)[column];
    let bVal: any = (b as any)[column];
    
    // Handle date columns
    if (column.toLowerCase().includes('date') || column === 'dateInitiated' || column === 'transactionDate') {
      const aDate = getDateField ? new Date(getDateField(a)) : new Date(aVal || 0);
      const bDate = getDateField ? new Date(getDateField(b)) : new Date(bVal || 0);
      return direction === 'asc' 
        ? aDate.getTime() - bDate.getTime() 
        : bDate.getTime() - aDate.getTime();
    }
    
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    return 0;
  });
}

// CSV Export utility function
export function exportToCsv<T>(
  transactions: T[],
  filename: string,
  headers: string[],
  getRow: (tx: T) => string[]
): void {
  const csvContent = [
    headers.join(','),
    ...transactions.map(tx => 
      getRow(tx).map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// The main component
function TransactionSearchFilter<T>({
  transactions,
  transactionTypes = [],
  searchQuery,
  onSearchQueryChange,
  dateRangeStart,
  dateRangeEnd,
  onDateRangeChange,
  typeFilter,
  onTypeFilterChange,
  sortColumn,
  sortDirection,
  onSortChange,
  isExpanded,
  onToggleExpand,
  csvFilename = 'transactions',
  csvHeaders,
  getCsvRow,
  filterFn,
  getDateField,
  title,
  additionalControls,
}: TransactionSearchFilterProps<T>): React.ReactElement {
  
  // Handle CSV export
  const handleExportCsv = useCallback(() => {
    if (!csvHeaders || !getCsvRow) return;
    
    // Apply current filters to get the data to export
    let filtered = transactions;
    if (filterFn) {
      filtered = transactions.filter(tx => 
        filterFn(tx, searchQuery, dateRangeStart, dateRangeEnd, typeFilter)
      );
    }
    
    exportToCsv(filtered, csvFilename, csvHeaders, getCsvRow);
  }, [transactions, csvFilename, csvHeaders, getCsvRow, filterFn, searchQuery, dateRangeStart, dateRangeEnd, typeFilter]);
  
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: isExpanded ? 2 : 0 }}>
        {title && (
          <Box sx={{ flexGrow: 1 }}>
            {/* Title is rendered by parent */}
          </Box>
        )}
        
        <Link
          component="button"
          variant="body2"
          onClick={onToggleExpand}
          sx={{ 
            cursor: 'pointer', 
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <FilterListIcon fontSize="small" />
          {isExpanded ? 'Hide' : 'Show'} Search and Sort
        </Link>
        
        {additionalControls}
      </Stack>
      
      <Collapse in={isExpanded}>
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center" 
          flexWrap="wrap"
          sx={{ 
            py: 2, 
            px: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            gap: 1,
          }}
        >
          {/* Search input */}
          <TextField
            size="small"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 200, flex: '1 1 200px' }}
          />
          
          {/* Start Date */}
          <TextField
            size="small"
            label="Start Date"
            type="date"
            value={dateRangeStart ? format(dateRangeStart, 'yyyy-MM-dd') : ''}
            onChange={(e) => onDateRangeChange(
              e.target.value ? new Date(e.target.value) : null,
              dateRangeEnd
            )}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          
          {/* End Date */}
          <TextField
            size="small"
            label="End Date"
            type="date"
            value={dateRangeEnd ? format(dateRangeEnd, 'yyyy-MM-dd') : ''}
            onChange={(e) => onDateRangeChange(
              dateRangeStart,
              e.target.value ? new Date(e.target.value) : null
            )}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          
          {/* Type Filter */}
          {transactionTypes.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type Filter</InputLabel>
              <Select
                value={typeFilter}
                label="Type Filter"
                onChange={(e) => onTypeFilterChange(e.target.value as string)}
              >
                <MenuItem value="">All Types</MenuItem>
                {transactionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {/* Export CSV Button */}
          {csvHeaders && getCsvRow && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
          )}
          
          {/* Clear Filters */}
          {(searchQuery || dateRangeStart || dateRangeEnd || typeFilter) && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                onSearchQueryChange('');
                onDateRangeChange(null, null);
                onTypeFilterChange('');
              }}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default TransactionSearchFilter;

// Hook for managing search/filter state
export function useTransactionSearchFilter() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRangeStart, setDateRangeStart] = useState<Date | null>(null);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const handleDateRangeChange = useCallback((start: Date | null, end: Date | null) => {
    setDateRangeStart(start);
    setDateRangeEnd(end);
  }, []);
  
  const handleSortChange = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
  }, []);
  
  const handleSortClick = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  }, [sortColumn, sortDirection]);
  
  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setDateRangeStart(null);
    setDateRangeEnd(null);
    setTypeFilter('');
  }, []);
  
  return {
    isExpanded,
    toggleExpand,
    searchQuery,
    setSearchQuery,
    dateRangeStart,
    dateRangeEnd,
    handleDateRangeChange,
    typeFilter,
    setTypeFilter,
    sortColumn,
    sortDirection,
    handleSortChange,
    handleSortClick,
    resetFilters,
    // Props to pass to the component
    filterProps: {
      isExpanded,
      onToggleExpand: toggleExpand,
      searchQuery,
      onSearchQueryChange: setSearchQuery,
      dateRangeStart,
      dateRangeEnd,
      onDateRangeChange: handleDateRangeChange,
      typeFilter,
      onTypeFilterChange: setTypeFilter,
      sortColumn,
      sortDirection,
      onSortChange: handleSortChange,
    },
  };
}
